import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

import { HiOutlineLockClosed } from "react-icons/hi";
import { TbWorldShare } from "react-icons/tb";
import { VerticalEllipsisIcon } from '../icons';
import { PinToggleIcon } from '../icons/PinToggleIcon';
import { DeleteIcon } from '../icons/DeleteIcon';

import { IChannel } from '../../services';
import { isChannelClosed } from '../../utils/chatClosed';
import { useAuth } from '../../contexts/AuthContext';
import { useCapabilities } from '../../contexts/permissions/useCapabilities';

interface ChatThumbnailMenuProps {
  chat: IChannel;
  isPinned: boolean;
  isPinning: boolean;
  isDeleting: boolean;
  isRenaming: boolean;
  isClosing?: boolean;
  isCloning?: boolean;
  enableShareChat?: boolean;
  /** False for closed chats or chats the current user does not own. */
  canRename?: boolean;
  onTogglePin: () => void;
  onDeleteClick: () => void;
  onCloseClick?: () => void;
  onCloneClick?: () => void;
  onRename: () => void;
  onShareChat: () => void;
}

const ChatThumbnailMenu: React.FC<ChatThumbnailMenuProps> = ({
  chat,
  isPinned,
  isPinning,
  isDeleting,
  isRenaming,
  isClosing = false,
  isCloning = false,
  enableShareChat = true,
  canRename = true,
  onTogglePin,
  onDeleteClick,
  onCloseClick,
  onCloneClick,
  onRename,
  onShareChat,
}) => {
  const isClosed = isChannelClosed(chat);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { canCloneChat } = useCapabilities();
  // REV-2207: delete and share are off for embedded (preauth) sessions. Not
  // capability flags — no tenant can grant them — so they key off is_external
  // rather than useCapabilities, and the backend refuses both with
  // `embedded_session_forbidden` regardless of what the UI shows.
  const { is_external } = useAuth();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top,
        left: rect.right + 4, // Position to the right of the button
      });
    }

    setIsOpen(!isOpen);
  };

  // Flip the menu upward when it would otherwise overflow past the bottom of the
  // viewport (e.g. opening it for the last chat in the list).
  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current || !buttonRef.current) return;

    const menuRect = menuRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const margin = 8;
    const overflow = menuRect.bottom - (window.innerHeight - margin);

    if (overflow > 0) {
      const flippedTop = Math.max(margin, buttonRect.bottom - menuRect.height);
      setMenuPosition((prev) =>
        prev.top === flippedTop ? prev : { ...prev, top: flippedTop },
      );
    }
  }, [isOpen]);

  const handleAction = (action: () => void, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    action();
    setIsOpen(false);
  };

  return (
    <>
      <div className="relative z-9999">
        {/* 3-dot menu button - compact but visible */}
        <button
          ref={buttonRef}
          onClick={handleMenuToggle}
          className="p-1 rounded-md hover:bg-hex-gray-200 transition-colors duration-200 text-hex-gray-500 hover:text-hex-gray-700 relative z-9999"
          title="More options"
        >
          <VerticalEllipsisIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Dropdown menu - rendered outside sidebar using portal */}
      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed w-48 bg-white border border-hex-gray-200 rounded-md shadow-lg z-9999"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1">
            {/* Pin/Unpin option */}
            <button
              onClick={(e) => handleAction(onTogglePin, e)}
              disabled={isPinning}
              className="w-full px-4 py-2 text-left text-md text-hex-gray-700 hover:bg-hex-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isPinning ? (
                <div className="w-4 h-4 border-2 border-hex-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <PinToggleIcon 
                  isPinned={isPinned} 
                  size={16} 
                  className="text-hex-gray-600"
                />
              )}
              <span>{isPinned ? 'Unpin chat' : 'Pin chat'}</span>
            </button>

            {enableShareChat && !is_external && (
              <button
                type="button"
                onClick={(e) => handleAction(onShareChat, e)}
                className="w-full px-4 py-2 text-left text-md text-hex-gray-700 hover:bg-hex-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {chat?.shared ? (
                  <TbWorldShare size={16} className="text-hex-gray-600" />
                ) : (
                  <HiOutlineLockClosed size={16} className="text-hex-gray-600" />
                )}
                <span>{chat?.shared ? 'Unshare chat' : 'Share chat'}</span>
              </button>
            )}

            {/* Rename option — only for open chats owned by the current user */}
            {canRename && (
              <button
                onClick={(e) => handleAction(onRename, e)}
                disabled={isRenaming}
                className="w-full px-4 py-2 text-left text-md text-hex-gray-700 hover:bg-hex-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isRenaming ? (
                  <div className="w-4 h-4 border-2 border-hex-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg 
                    className="w-4 h-4 text-hex-gray-600" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" 
                    />
                  </svg>
                )}
                <span>{isRenaming ? 'Renaming...' : 'Rename'}</span>
              </button>
            )}

            {/* Clone — use "Clone to continue" in the chat panel when viewing a closed chat */}
            {canCloneChat && onCloneClick && !isClosed && (
              <button
                onClick={(e) => handleAction(onCloneClick, e)}
                disabled={isCloning}
                className="w-full px-4 py-2 text-left text-md text-hex-gray-700 hover:bg-hex-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isCloning ? (
                  <div className="w-4 h-4 border-2 border-hex-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg
                    className="w-4 h-4 text-hex-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                    />
                  </svg>
                )}
                <span>{isCloning ? 'Cloning...' : 'Clone chat'}</span>
              </button>
            )}

            {/* Close chat option — hidden if already closed */}
            {onCloseClick && !isClosed && (
              <button
                onClick={(e) => handleAction(onCloseClick, e)}
                disabled={isClosing}
                className="w-full px-4 py-2 text-left text-md text-hex-gray-700 hover:bg-hex-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isClosing ? (
                  <div className="w-4 h-4 border-2 border-hex-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <HiOutlineLockClosed size={16} className="text-hex-gray-600" />
                )}
                <span>{isClosing ? 'Closing...' : 'Close chat'}</span>
              </button>
            )}

            {/* Delete option — withheld from embedded sessions (REV-2207) */}
            {!is_external && (
              <button
                onClick={(e) => handleAction(onDeleteClick, e)}
                disabled={isDeleting}
                className="w-full px-4 py-2 text-left text-md text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <DeleteIcon size={16} className="text-red-600" />
                )}
                <span>Delete chat</span>
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ChatThumbnailMenu;
